/**
 * ENFAMI Premium Immersive — Three.js lazy, scrollytelling, glare, trail, gyro, son
 */
(function () {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MOBILE = window.matchMedia('(max-width: 989px)').matches;
  const LOW_CORES = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;
  const LITE = REDUCED || (MOBILE && LOW_CORES);

  if (LITE) document.body.classList.add('enfami-immersive-lite');

  let audioCtx = null;
  let audioEnabled = false;

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function enableAudioOnce() {
    if (audioEnabled || LITE) return;
    document.addEventListener(
      'click',
      () => {
        try {
          audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
          audioEnabled = true;
        } catch (e) {}
      },
      { once: true }
    );
  }

  function playTick(freq) {
    if (!audioEnabled || !audioCtx) return;
    const o = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    o.frequency.value = freq || 520;
    g.gain.value = 0.04;
    o.connect(g);
    g.connect(audioCtx.destination);
    o.start();
    g.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.12);
    o.stop(audioCtx.currentTime + 0.12);
  }

  /* ── Split text ── */
  function initSplitText() {
    document.querySelectorAll('[data-split-text]').forEach((el) => {
      if (el.dataset.splitDone) return;
      el.dataset.splitDone = 'true';
      const text = el.textContent.trim();
      el.textContent = '';
      el.setAttribute('aria-label', text);
      text.split(/\s+/).forEach((word, wi) => {
        const w = document.createElement('span');
        w.className = 'split-word';
        w.style.display = 'inline-block';
        w.style.whiteSpace = 'nowrap';
        word.split('').forEach((ch) => {
          const s = document.createElement('span');
          s.className = 'split-char';
          s.textContent = ch;
          s.setAttribute('aria-hidden', 'true');
          w.appendChild(s);
        });
        el.appendChild(w);
        if (wi < text.split(/\s+/).length - 1) el.appendChild(document.createTextNode(' '));
      });
    });

    const gsap = window.gsap;
    const ST = window.ScrollTrigger;
    if (!gsap || !ST) return;

    document.querySelectorAll('[data-split-text]').forEach((el) => {
      const chars = el.querySelectorAll('.split-char');
      if (!chars.length) return;
      gsap.fromTo(
        chars,
        { opacity: 0, y: 24, rotateX: -40 },
        {
          opacity: 1,
          y: 0,
          rotateX: 0,
          duration: 0.55,
          stagger: 0.025,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' },
        }
      );
    });
  }

  /* ── Parallax 3D souris ── */
  function initMouseParallax() {
    if (MOBILE || REDUCED) return;

    document.querySelectorAll('[data-mouse-parallax]').forEach((root) => {
      const layers = root.querySelectorAll('[data-depth]');
      root.addEventListener('mousemove', (e) => {
        const rect = root.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        layers.forEach((layer) => {
          const d = parseFloat(layer.getAttribute('data-depth') || '0.03');
          layer.style.transform = `translate3d(${x * d * 120}px, ${y * d * 120}px, 0)`;
        });
      });
      root.addEventListener('mouseleave', () => {
        layers.forEach((layer) => {
          layer.style.transform = '';
        });
      });
    });
  }

  /* ── Hero slideshow ── */
  function initHeroSlideshow() {
    document.querySelectorAll('[data-hero-slideshow]').forEach((hero) => {
      const root = hero.querySelector('[data-slideshow]');
      if (!root || root.dataset.slideshowInit === 'true') return;
      root.dataset.slideshowInit = 'true';

      const slides = [...root.querySelectorAll('[data-slide]')];
      const dots = [...root.querySelectorAll('[data-slide-to]')];
      if (slides.length <= 1) return;

      const delay = (parseInt(hero.getAttribute('data-autoplay') || '4', 10) || 4) * 1000;
      let current = 0;
      let timer = null;

      function goTo(index) {
        current = (index + slides.length) % slides.length;
        slides.forEach((s, i) => s.classList.toggle('is-active', i === current));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
      }

      function next() {
        goTo(current + 1);
      }

      function start() {
        stop();
        timer = setInterval(next, delay);
      }

      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
      }

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          goTo(parseInt(dot.getAttribute('data-slide-to') || '0', 10));
          start();
        });
      });

      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);

      if (!REDUCED) start();
    });
  }

  /* ── Three.js hero maillot (optionnel) ── */
  async function initHero3D() {
    const hero = document.querySelector('[data-hero-3d]');
    if (!hero || LITE) return;

    const canvas = hero.querySelector('.eph-3d__canvas');
    const glbUrl = hero.getAttribute('data-glb');
    const activeSlide = hero.querySelector('[data-slide].is-active img');
    const texUrl = hero.getAttribute('data-texture') || activeSlide?.currentSrc || activeSlide?.src;
    if (!canvas || (!texUrl && !glbUrl)) return;

    try {
      await loadScript('https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.min.js');
    } catch (e) {
      return;
    }

    const THREE = window.THREE;
    if (!THREE) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.z = 3.2;

    const amb = new THREE.AmbientLight(0xffffff, 0.65);
    const key = new THREE.DirectionalLight(0xc8ff00, 1.1);
    key.position.set(2, 3, 4);
    const rim = new THREE.DirectionalLight(0x00ffd5, 0.45);
    rim.position.set(-3, 1, -2);
    scene.add(amb, key, rim);

    let mesh = null;
    const group = new THREE.Group();
    scene.add(group);

    function addTexturedPlane(url) {
      const loader = new THREE.TextureLoader();
      loader.load(url, (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        const geo = new THREE.PlaneGeometry(1.6, 2, 32, 32);
        const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.45, metalness: 0.15, side: THREE.DoubleSide });
        mesh = new THREE.Mesh(geo, mat);
        group.add(mesh);
      });
    }

    if (glbUrl) {
      try {
        await loadScript('https://cdn.jsdelivr.net/npm/three@0.160.0/examples/js/loaders/GLTFLoader.js');
        const loader = new THREE.GLTFLoader();
        loader.load(glbUrl, (gltf) => {
          mesh = gltf.scene;
          mesh.scale.set(1.4, 1.4, 1.4);
          group.add(mesh);
        });
      } catch (e) {
        if (texUrl) addTexturedPlane(texUrl);
      }
    } else if (texUrl) {
      addTexturedPlane(texUrl);
    }

    let mx = 0;
    let my = 0;
    let rx = 0;
    let ry = 0;
    let scrollRot = 0;

    hero.addEventListener('mousemove', (e) => {
      const rect = canvas.getBoundingClientRect();
      mx = ((e.clientX - rect.left) / rect.width - 0.5) * 0.7;
      my = ((e.clientY - rect.top) / rect.height - 0.5) * 0.5;
    });

    document.addEventListener('enfami:scroll', (e) => {
      scrollRot = (e.detail.scroll || window.scrollY) * 0.0004;
    });

    function resize() {
      const rect = canvas.getBoundingClientRect();
      const w = rect.width || hero.clientWidth;
      const h = rect.height || hero.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }

    resize();
    window.addEventListener('resize', resize);

    let rafId = 0;
    let running = true;

    function tick() {
      if (!running) return;
      rx += (my - rx) * 0.08;
      ry += (mx - ry) * 0.08;
      group.rotation.x = rx;
      group.rotation.y = ry + scrollRot;
      group.position.y = Math.sin(scrollRot * 2) * 0.08;
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    }
    tick();

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) {
          running = false;
          cancelAnimationFrame(rafId);
          rafId = 0;
        } else if (!running) {
          running = true;
          tick();
        }
      },
      { threshold: 0.05 }
    );
    io.observe(hero);

    /* Gyro mobile */
    if (MOBILE && window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', (e) => {
        if (e.beta == null || e.gamma == null) return;
        mx = (e.gamma / 90) * 0.5;
        my = ((e.beta - 45) / 90) * 0.35;
      });
    }
  }

  /* ── Fond body transition entre sections ── */
  function initBgTransitions() {
    const gsap = window.gsap;
    const ST = window.ScrollTrigger;
    if (!gsap || !ST) return;

    document.body.classList.add('enfami-bg-transition');
    document.querySelectorAll('[data-section-bg]').forEach((sec) => {
      const bg = sec.getAttribute('data-section-bg');
      if (!bg) return;
      ST.create({
        trigger: sec,
        start: 'top 60%',
        end: 'bottom 40%',
        onEnter: () => {
          document.body.style.backgroundColor = bg;
        },
        onEnterBack: () => {
          document.body.style.backgroundColor = bg;
        },
      });
    });
  }

  /* ── Glare holographique ── */
  function initGlare() {
    if (MOBILE || REDUCED) return;

    document.querySelectorAll('.enfami-carousel__card, product-card, .product-card, [data-glare]').forEach((card) => {
      let wrap = card.querySelector('.enfami-glare-wrap');
      const target = card.querySelector('.enfami-carousel__img-wrap, .product-card-gallery, .card-gallery') || card;
      if (!wrap) {
        wrap = document.createElement('div');
        wrap.className = 'enfami-glare-wrap';
        if (target.parentNode === card) {
          card.insertBefore(wrap, target);
          wrap.appendChild(target);
        } else {
          target.classList.add('enfami-glare-wrap');
          wrap = target;
        }
      }
      card.addEventListener('mousemove', (e) => {
        const rect = wrap.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        wrap.style.setProperty('--glare-x', x + '%');
        wrap.style.setProperty('--glare-y', y + '%');
        wrap.classList.add('is-glare-active');
      });
      card.addEventListener('mouseleave', () => wrap.classList.remove('is-glare-active'));
    });
  }

  /* ── CTA magnetic + morph ── */
  function initMorphCTA() {
    document.querySelectorAll('.enfami-cta-morph, .eph-3d__cta').forEach((btn) => {
      btn.addEventListener('mouseenter', () => btn.classList.add('is-morphed'));
      btn.addEventListener('mouseleave', () => btn.classList.remove('is-morphed'));
      btn.addEventListener('mousedown', () => {
        btn.classList.add('is-morphed');
        playTick(640);
      });
    });
  }

  /* ── Trail curseur zones clés ── */
  function initCursorTrail() {
    /* Curseur natif — trail désactivé */
  }

  /* ── Three.js page produit (aperçu tilt) ── */
  async function initProduct3D() {
    return; /* Désactivé — galerie native uniquement (maquette PDP) */
    const main = document.querySelector('#MainContent');
    if (!main || !String(main.dataset.template || '').includes('product') || LITE) return;

    const gallery = document.querySelector('media-gallery');
    const img = gallery?.querySelector('img[src*="cdn.shopify"]');
    if (!gallery || !img) return;

    const wrap = document.createElement('div');
    wrap.className = 'ep-product-3d';
    wrap.setAttribute('data-product-3d', '');
    wrap.setAttribute('data-texture', img.currentSrc || img.src);
    wrap.innerHTML = '<canvas class="ep-product-3d__canvas" aria-hidden="true"></canvas>';
    gallery.style.position = 'relative';
    gallery.appendChild(wrap);

    const hero = wrap;
    const canvas = wrap.querySelector('.ep-product-3d__canvas');
    const texUrl = hero.getAttribute('data-texture');
    if (!canvas || !texUrl) return;

    try {
      await loadScript('https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.min.js');
    } catch (e) {
      wrap.remove();
      return;
    }

    const THREE = window.THREE;
    if (!THREE) {
      wrap.remove();
      return;
    }

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.z = 2.8;
    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const key = new THREE.DirectionalLight(0xc8ff00, 0.9);
    key.position.set(2, 2, 3);
    scene.add(key);

    const group = new THREE.Group();
    scene.add(group);

    new THREE.TextureLoader().load(texUrl, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(1.4, 1.75, 24, 24),
        new THREE.MeshStandardMaterial({ map: tex, roughness: 0.5, metalness: 0.12, side: THREE.DoubleSide })
      );
      group.add(mesh);
    });

    let mx = 0;
    let my = 0;
    let rx = 0;
    let ry = 0;
    let running = true;

    wrap.addEventListener('mousemove', (e) => {
      const rect = wrap.getBoundingClientRect();
      mx = ((e.clientX - rect.left) / rect.width - 0.5) * 0.55;
      my = ((e.clientY - rect.top) / rect.height - 0.5) * 0.4;
    });
    wrap.addEventListener('mouseleave', () => {
      mx = 0;
      my = 0;
    });

    if (MOBILE && window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', (e) => {
        if (e.beta == null || e.gamma == null) return;
        mx = (e.gamma / 90) * 0.45;
        my = ((e.beta - 45) / 90) * 0.3;
      });
    }

    function resize() {
      const w = wrap.clientWidth || 280;
      const h = wrap.clientHeight || 340;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }

    resize();
    window.addEventListener('resize', resize);

    function tick() {
      if (!running) return;
      rx += (my - rx) * 0.1;
      ry += (mx - ry) * 0.1;
      group.rotation.x = rx;
      group.rotation.y = ry;
      renderer.render(scene, camera);
      requestAnimationFrame(tick);
    }
    tick();

    const io = new IntersectionObserver(
      (entries) => {
        running = entries[0].isIntersecting;
        if (running) tick();
      },
      { threshold: 0.1 }
    );
    io.observe(wrap);
  }

  /* ── Son ATC ── */
  function initCartSound() {
    document.addEventListener('cart:update', (e) => {
      if (e.detail?.data?.didError) return;
      playTick(880);
    });
  }

  function boot() {
    enableAudioOnce();
    initSplitText();
    initMouseParallax();
    initGlare();
    initMorphCTA();
    initCursorTrail();
    initCartSound();
    initHeroSlideshow();

    const runHeavy = () => {
      initHero3D();
      initBgTransitions();
      initProduct3D();
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    };

    if (window.gsap && window.ScrollTrigger) {
      runHeavy();
    } else {
      document.addEventListener('enfami:premium-ready', runHeavy, { once: true });
      setTimeout(runHeavy, 1200);
    }
  }

  window.enfamiImmersiveRefresh = boot;

  document.addEventListener('enfami:premium-ready', () => {
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
